import { SITE_INSIDE, SITE_PLACE } from "../../convex/constants/siteAccess.lib";
import type { SiteAccess } from "../../convex/schemas/validators/siteAccess.lib";
import { needsLiftgate } from "../freight/site-access-service-options";

type DockSchedule = "fcfs" | "appointment" | "other";

interface LegacyPickupEquipment {
  hasForklift: boolean;
  hasLoadingDock: boolean;
  hasPalletJack?: boolean;
}

export interface LegacySiteFacts {
  pickupEquipment?: LegacyPickupEquipment;
  dockScheduleType?: DockSchedule;
}

export interface SiteAccessHolder extends LegacySiteFacts {
  siteAccess?: SiteAccess;
}

function definedOnly(access: SiteAccess): SiteAccess {
  const entries = Object.entries(access).filter(([, value]) => value !== undefined);
  return Object.fromEntries(entries);
}

function appointmentFromSchedule(schedule: DockSchedule | undefined): boolean | undefined {
  if (schedule === "appointment") return true;
  if (schedule === "fcfs") return false;
  return undefined;
}

export function siteAccessFromLegacy(facts: LegacySiteFacts): SiteAccess | undefined {
  const equipment = facts.pickupEquipment;
  const appointment = appointmentFromSchedule(facts.dockScheduleType);
  if (!equipment && appointment === undefined) return undefined;
  return definedOnly({
    dock: equipment?.hasLoadingDock,
    forklift: equipment?.hasForklift,
    palletJack: equipment?.hasPalletJack,
    appointment,
  });
}

export function resolveSiteAccess(holder: SiteAccessHolder): SiteAccess | undefined {
  return holder.siteAccess ?? siteAccessFromLegacy(holder);
}

export function legacyFactsFromSiteAccess(access: SiteAccess, current: DockSchedule | undefined): Required<Pick<LegacySiteFacts, "pickupEquipment">> & LegacySiteFacts {
  const scheduleFromAccess: DockSchedule | undefined =
    access.appointment === true ? "appointment" : access.appointment === false ? "fcfs" : undefined;
  return {
    pickupEquipment: {
      hasForklift: access.forklift === true,
      hasLoadingDock: access.dock === true,
      ...(access.palletJack === undefined ? {} : { hasPalletJack: access.palletJack }),
    },
    dockScheduleType: scheduleFromAccess ?? current,
  };
}

export function normalizeSiteAccess(access: SiteAccess): SiteAccess {
  const limitedAccessKind = access.place === SITE_PLACE.LIMITED_ACCESS ? access.limitedAccessKind : undefined;
  const inside = access.inside === SITE_INSIDE.NONE ? undefined : access.inside;
  return definedOnly({ ...access, limitedAccessKind, inside });
}

export interface StopAccessFacts {
  dock?: boolean;
  liftgate?: boolean;
  residential?: boolean;
  appointment?: boolean;
  insideDelivery?: boolean;
  loadingHelp?: boolean;
  forklift?: boolean;
}

export function stopAccessFacts(access: SiteAccess | undefined): StopAccessFacts {
  if (!access) return {};
  const facts: StopAccessFacts = {
    dock: access.dock,
    liftgate: access.dock === undefined && access.place === undefined ? undefined : needsLiftgate(access),
    residential: access.place === undefined ? undefined : access.place === SITE_PLACE.RESIDENCE,
    appointment: access.appointment,
    insideDelivery: access.inside === undefined ? undefined : access.inside !== SITE_INSIDE.NONE,
    loadingHelp: access.driverAssist,
    forklift: access.forklift,
  };
  return Object.fromEntries(Object.entries(facts).filter(([, value]) => value !== undefined));
}

export function siteAccessKey(access: SiteAccess | undefined): string {
  if (!access) return "";
  const normalized = normalizeSiteAccess(access);
  const vehicles = normalized.vehicles ? [...normalized.vehicles].sort() : undefined;
  return JSON.stringify(Object.entries({ ...normalized, vehicles }).filter(([, value]) => value !== undefined).sort(([left], [right]) => (left < right ? -1 : 1)));
}

export function answeredOnSave(access: SiteAccess): SiteAccess {
  return { ...access, dock: access.dock ?? false, forklift: access.forklift ?? false };
}
